
import { getFirestore, collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { app } from "../../firebase-config.js";

const db = getFirestore(app);

export const AdminAnalyticsPage = async () => {
    const container = document.createElement('div');
    container.className = "max-w-5xl mx-auto px-4 py-8 pb-32";

    container.innerHTML = `
        <div class="mb-8 flex items-center justify-between">
            <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span class="material-symbols-outlined text-indigo-600">trending_up</span>
                </div>
                <div>
                    <h1 class="text-2xl font-bold text-gray-900">트렌드 분석 센터</h1>
                    <p class="text-sm text-gray-500">실시간 해시태그 유입 현황 (최근 200건 기준)</p>
                </div>
            </div>
            <button id="btn-refresh" class="text-sm bg-white border border-gray-200 shadow-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-1 active:scale-95 transition-transform">
                <span class="material-symbols-outlined text-[16px]">refresh</span> 새로고침
            </button>
        </div>

        <!-- Top Trends (Chart) -->
        <div class="bg-white rounded-2xl shadow-sm border border-indigo-100 p-6 mb-8 relative overflow-hidden">
            <div class="absolute top-0 right-0 p-3 opacity-10">
                <span class="material-symbols-outlined text-9xl">tag</span>
            </div>
            
            <h2 class="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2 relative z-10">
                <span class="w-2 h-6 bg-indigo-500 rounded-full"></span>
                지금 뜨는 키워드 TOP 5
            </h2>

            <!-- Bar Chart Container -->
            <div id="trend-chart" class="space-y-4 relative z-10">
                <div class="animate-pulse flex space-y-4 flex-col">
                    <div class="h-8 bg-gray-100 rounded w-full"></div>
                    <div class="h-8 bg-gray-100 rounded w-3/4"></div>
                    <div class="h-8 bg-gray-100 rounded w-1/2"></div>
                </div>
            </div>
        </div>

        <!-- Detailed Table -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100">
                 <h3 class="text-sm font-bold text-gray-700">전체 키워드 순위</h3>
            </div>
            <div class="overflow-x-auto">
                <table class="w-full text-left border-collapse">
                    <thead>
                        <tr class="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase">
                            <th class="px-6 py-3 font-semibold text-center w-16">순위</th>
                            <th class="px-6 py-3 font-semibold">해시태그</th>
                            <th class="px-6 py-3 font-semibold text-right">언급 수</th>
                            <th class="px-6 py-3 font-semibold text-right">점유율</th>
                        </tr>
                    </thead>
                    <tbody id="analytics-list" class="divide-y divide-gray-100 text-sm">
                        <tr>
                            <td colspan="4" class="px-6 py-10 text-center text-gray-400">데이터 로딩 중...</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    `;

    const chartContainer = container.querySelector('#trend-chart');
    const listContainer = container.querySelector('#analytics-list');
    const btnRefresh = container.querySelector('#btn-refresh');

    const loadAnalytics = async () => {
        // Reset UI
        chartContainer.innerHTML = `
             <div class="animate-pulse flex space-y-4 flex-col">
                <div class="h-8 bg-gray-100 rounded w-full"></div>
                <div class="h-8 bg-gray-100 rounded w-3/4"></div>
            </div>
        `;
        listContainer.innerHTML = '<tr><td colspan="4" class="px-6 py-10 text-center text-gray-400">데이터 분석 중...</td></tr>';

        try {
            // 1. Fetch recent 200 posts
            const q = query(collection(db, 'plaza_feeds'), orderBy('createdAt', 'desc'), limit(200));
            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                chartContainer.innerHTML = '<p class="text-gray-400">분석할 데이터가 없습니다.</p>';
                listContainer.innerHTML = '<tr><td colspan="4" class="px-6 py-10 text-center text-gray-400">데이터 없음</td></tr>';
                return;
            }

            // 2. Aggregate Tags
            const tagMap = {};
            let totalTagsDetected = 0;

            snapshot.forEach(docSnap => {
                const data = docSnap.data();
                if (data.hashtags && Array.isArray(data.hashtags)) {
                    data.hashtags.forEach(tag => {
                        const cleanTag = tag.trim(); // Keep #
                        if (cleanTag) {
                            tagMap[cleanTag] = (tagMap[cleanTag] || 0) + 1;
                            totalTagsDetected++;
                        }
                    });
                }
            });

            // 3. Sort
            // Convert to array: [{ tag: '#abc', count: 10 }]
            const sortedTags = Object.entries(tagMap)
                .map(([tag, count]) => ({ tag, count }))
                .sort((a, b) => b.count - a.count);

            if (sortedTags.length === 0) {
                chartContainer.innerHTML = '<p class="text-gray-400">해시태그가 포함된 게시글이 없습니다. (#태그를 써보세요!)</p>';
                listContainer.innerHTML = '<tr><td colspan="4" class="px-6 py-10 text-center text-gray-400">해시태그 없음</td></tr>';
                return;
            }

            // 4. Render Chart (Top 5)
            const top5 = sortedTags.slice(0, 5);
            const maxCount = top5[0].count; // For bar width calculation

            chartContainer.innerHTML = top5.map((item, index) => {
                const percentage = Math.round((item.count / totalTagsDetected) * 100);
                const barWidth = Math.max(10, Math.round((item.count / maxCount) * 100)); // Min 10% width

                // Color Palette
                const colors = ['bg-indigo-500', 'bg-blue-500', 'bg-cyan-500', 'bg-teal-500', 'bg-purple-500'];
                const color = colors[index % colors.length];

                return `
                    <div class="group relative">
                        <div class="flex justify-between text-xs text-gray-500 mb-1 font-bold px-1">
                            <span class="text-indigo-800">${index + 1}. ${item.tag}</span>
                            <span>${item.count}회 (${percentage}%)</span>
                        </div>
                        <div class="w-full bg-gray-100 rounded-full h-4 overflow-hidden shadow-inner">
                            <div class="h-full ${color} rounded-full transition-all duration-1000 ease-out group-hover:brightness-110 relative" style="width: 0%" data-width="${barWidth}%">
                                <div class="absolute inset-0 bg-white opacity-20 bg-[length:10px_10px] bg-[linear-gradient(45deg,rgba(255,255,255,0.2)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.2)_50%,rgba(255,255,255,0.2)_75%,transparent_75%,transparent)] animate-[pulse_2s_infinite]"></div>
                            </div>
                        </div>
                    </div>
                `;
            }).join('');

            // Trigger Animation
            setTimeout(() => {
                chartContainer.querySelectorAll('[data-width]').forEach(el => {
                    el.style.width = el.getAttribute('data-width');
                });
            }, 100);

            // 5. Render Table (All)
            let html = '';
            sortedTags.forEach((item, index) => {
                const percentage = ((item.count / totalTagsDetected) * 100).toFixed(1);
                const isTop3 = index < 3;

                html += `
                    <tr class="hover:bg-gray-50 transition-colors">
                        <td class="px-6 py-4 text-center">
                            ${isTop3
                        ? `<span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 text-yellow-700 font-bold text-xs">${index + 1}</span>`
                        : `<span class="text-gray-500">${index + 1}</span>`}
                        </td>
                        <td class="px-6 py-4">
                            <span class="font-bold text-gray-800 cursor-pointer hover:text-indigo-600 hover:underline" onclick="window.open('/#/plaza?tag=${item.tag.slice(1)}', '_blank')">${item.tag}</span>
                        </td>
                        <td class="px-6 py-4 text-right font-mono text-gray-600">
                            ${item.count.toLocaleString()}
                        </td>
                        <td class="px-6 py-4 text-right text-gray-500 text-xs">
                             <div class="flex items-center justify-end gap-2">
                                <div class="w-16 bg-gray-100 h-1.5 rounded-full overflow-hidden">
                                    <div class="h-full bg-gray-400" style="width: ${percentage}%"></div>
                                </div>
                                <span class="w-8">${percentage}%</span>
                            </div>
                        </td>
                    </tr>
                `;
            });
            listContainer.innerHTML = html;

        } catch (e) {
            console.error(e);
            chartContainer.innerHTML = `<div class="p-4 bg-red-50 text-red-600 rounded">분석 오류: ${e.message}</div>`;
            listContainer.innerHTML = '<tr><td colspan="4" class="px-6 py-10 text-center text-red-400">데이터 로드 실패</td></tr>';
        }
    };

    btnRefresh.onclick = () => {
        btnRefresh.classList.add('animate-spin'); // simple spin class logic if exists, or just manual
        loadAnalytics().then(() => btnRefresh.classList.remove('animate-spin'));
    };

    // Initial Load
    loadAnalytics();

    return container;
};
